import ContentUpload from 'module/ContentUpload/ContentUpload';

import React, { useEffect } from 'react';

import { Alert, Button, Card, Form, Input } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetArticleContentQuery, useGetCurrentUserQuery, useGetOfferQuery, useSendCommitteeControlDecisionMutation } from 'services';
import { useApiErrorMessage, useForeignAuthorScreenGuard } from 'hooks';
import { fetchError } from 'slices/commonSlice';
import { committeeDecisions } from 'utils';

import AddMediaContent from './AddMediaContent';
import MediaActionProvider from './AddMediaContent/Context/MediaActioncontext';

/**
 * Tercümenin gönderimi bu uca gider ve maddeyi İH - Madde Kontrol'e döndürür. Geri çekme madde
 * detayındaki `TranslationStepActions`'tadır.
 */
const TRANSLATION_DECISION_COMMITTEE = 'committee_translation';

/**
 * @description İH - Tercüme aşamasının gönderim ekranı. Yabancı dildeki madde dosyasını sorumlu ilim
 * heyetinin başkanı ya da sekreteri indirir, Türkçe çevirisini aynı dosyanın yeni sürümü olarak yükler
 * ve maddeyi geri gönderir; madde İH - Madde Kontrol aşamasına döner.
 *
 * Route herkese açık olduğu için ekran kendi yetki kontrolünü `useForeignAuthorScreenGuard` ile yapar:
 * madde yabancı yazar sürecinde değilse ya da kullanıcı sorumlu heyette başkan/sekreter değilse madde
 * detayına yönlendirilir.
 */
const TranslationSendItemsView = () => {
  const { id } = useParams();
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const getErrorMessage = useApiErrorMessage();
  const { data: current_user } = useGetCurrentUserQuery();
  const { data: offer, isLoading: isOfferLoading } = useGetOfferQuery(id);
  const [sendCommitteeControlDecision] = useSendCommitteeControlDecisionMutation();

  const { data } = useGetArticleContentQuery(
    {
      offerID: id,
    },
    {
      skip: !current_user,
    }
  );

  const itemFile = Form.useWatch('itemFile', form);

  useForeignAuthorScreenGuard(offer, { isLoading: isOfferLoading });

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        itemFile: data.contents.find((item) => item.type === 0),
        idCard: data.contents.find((item) => item.type === 1),
      });
    }
  }, [data, form]);

  useEffect(() => {
    if (document) {
      // buradaki id degeri /src/routes/index.js dosyasindaki div in id degerini ifade ediyor
      const element = document.getElementById('routes-wrapper');
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, []);

  const handleSendTranslation = async (values) => {
    const { error } = await sendCommitteeControlDecision({
      id,
      committee: TRANSLATION_DECISION_COMMITTEE,
      body: {
        status: committeeDecisions.approved,
        // aciklama zorunlu degil; bos birakildiginda govdeye hic eklenmez
        ...(values.description ? { description: values.description } : {}),
      },
    });

    if (error) return dispatch(fetchError(getErrorMessage(error)));

    navigate(`/madde-detay/${id}`);
  };

  return (
    <>
      <Alert
        className="gx-mb-3"
        type="info"
        showIcon
        message="Tercüme Yükleme"
        description="Yabancı dildeki madde dosyasını indirip Türkçe tercümesini aynı alana yükleyin; yüklediğiniz dosya maddenin yeni sürümü olarak kaydedilir, önceki sürüm silinmez. Gönderdiğinizde madde İH - Madde Kontrol aşamasına döner ve oradaki kararlar yeniden verilebilir hâle gelir."
      />

      <div className="gx-d-flex gx-justify-content-end">
        <Button
          onClick={() => navigate(-1)}
          type="default"
          style={{
            fontSize: 18,
          }}
        >
          <ArrowLeftOutlined />
          {formatMessage({
            id: 'goBack',
          })}
        </Button>
      </div>
      <Form
        id="translation-send-items-form"
        layout="vertical"
        onFinish={handleSendTranslation}
        form={form}
      >
        <ContentUpload
          id={id}
          articleTypeID={offer?.articleType?.id}
          allowItemFileDownload
        />
        <Card>
          <MediaActionProvider>
            <AddMediaContent offerID={id} />
          </MediaActionProvider>
        </Card>
        <Form.Item
          className="gx-mb-4"
          label="Açıklamalar ve Notlar"
          name="description"
          rules={[
            {
              min: 50,
              message: formatMessage({
                id: 'article.reasonLengthMessageShort',
              }),
            },
          ]}
          normalize={(value) => (value.trim() === '' ? undefined : value)}
        >
          <Input.TextArea
            placeholder="Notunuz varsa girebilirsiniz"
            rows={5}
            showCount
          />
        </Form.Item>
        <Form.Item>
          <Button
            block
            disabled={!itemFile}
            htmlType="submit"
            type="primary"
          >
            {formatMessage({
              id: 'article.status.sendItem',
            })}
          </Button>
        </Form.Item>
      </Form>

    </>
  );
};

export default TranslationSendItemsView;
